import { useCallback } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  fetchAdminAccounts,
  getAccountBillingInfo,
  postNamedCommand,
  postNamedQuery,
  saveAccountTags
} from '../service'
import { useAppContext } from '../redux/slices/appContext'
import { QUERY_KEYS } from './queryKeys'

export const useSearchAccountsMultiple = (query, queryOptions = {}) => {
  const { userId } = useAppContext()
  const { mapper, enabled } = queryOptions
  return useQuery({
    queryKey: [QUERY_KEYS.searchAccountsMultiple, userId, query],
    queryFn: async () => {
      const { data } = await postNamedQuery('levels', 'searchAccountsMultiple', query)
      return data
    },
    select: mapper,
    enabled
  })
}

export const useInvalidateSearchAccountsMultiple = () => {
  const client = useQueryClient()
  const { userId } = useAppContext()
  return useCallback(() => {
    console.debug('invalidating', QUERY_KEYS.searchAccountsMultiple)
    client.invalidateQueries({
      queryKey: [QUERY_KEYS.searchAccountsMultiple, userId],
      refetchType: 'all'
    }).catch(console.error)
  }, [client, userId])
}

export const useSearchAccounts = (query, options = {}) => {
  const { userId } = useAppContext()
  const { enabled = true, mapper = null } = options
  return useQuery({
    queryKey: [QUERY_KEYS.searchAccounts, userId, query],
    queryFn: async ({ signal }) => {
      const { data } = await postNamedQuery(
        'levels',
        'searchAccounts',
        query,
        signal
      )
      return mapper ? mapper(data) : data
    },
    enabled
  })
}

export const useAccountBillingInfo = (query, options = {}) => {
  const { userId } = useAppContext()
  const { enabled = true, mapper = null } = options
  return useQuery({
    queryKey: [QUERY_KEYS.accountBillingInfo, userId, query],
    queryFn: async () => {
      const { data } = await getAccountBillingInfo(query)
      return mapper ? mapper(data) : data
    },
    enabled
  })
}

export const useAccounts = (query, mapper = null) => {
  const { userId } = useAppContext()
  return useQuery({
    queryKey: [QUERY_KEYS.accounts, userId, query],
    queryFn: async () => {
      const { data } = await fetchAdminAccounts(query)
      return mapper ? mapper(data) : data
    }
  })
}

export const useListAssignedClientAccounts = (clientId) => {
  const { userId } = useAppContext()
  return useQuery({
    queryKey: [QUERY_KEYS.listAssignedClientAccounts, userId, clientId],
    queryFn: async () => {
      const { data } = await postNamedQuery('levels', 'list-client-accounts', {
        clientId
      })

      return data
    }
  })
}

export const useInvalidateListAssignedClientAccounts = (clientId) => {
  const client = useQueryClient()
  const { userId } = useAppContext()
  return useCallback(() => {
    console.debug('invalidating', QUERY_KEYS.listAssignedClientAccounts)
    client.invalidateQueries({
      queryKey: [QUERY_KEYS.listAssignedClientAccounts, userId, clientId],
      exact: true
    }).catch(console.error)
  }, [client, clientId, userId])
}

export const useListTaxStatusOptions = () => {
  const { userId } = useAppContext()
  return useQuery({
    queryKey: [QUERY_KEYS.listTaxStatusOptions, userId],
    queryFn: async () => {
      const { data } = await postNamedQuery('accounts', 'list-tax-status-options', {})
      return data
    }
  })
}

export const useModifyAccountTaxStatusMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ accountId, taxStatusId }) => {
      const { data } = await postNamedCommand('accounts', 'updateAccountDefaultStatus', {
        accountId,
        taxStatusId
      })

      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.searchAccounts], refetchType: 'all' }).catch(console.error)
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.searchAccountsMultiple], refetchType: 'all' }).catch(console.error)
    }
  })
}

export const useModifyAccountDisplayNameMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ accountId, displayName }) => {
      const { data } = await postNamedCommand('accounts', 'updateAccountDisplayName', {
        accountId,
        displayName
      })

      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.searchAccounts], refetchType: 'all' }).catch(console.error)
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.searchAccountsMultiple], refetchType: 'all' }).catch(console.error)
    }
  })
}

export const useModifyAccountTagsMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ accountId, tags }) => {
      const { data } = await saveAccountTags(accountId, tags)

      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.searchAccounts], refetchType: 'all' }).catch(console.error)
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.searchAccountsMultiple], refetchType: 'all' }).catch(console.error)
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.searchGroups], refetchType: 'all' }).catch(console.error)
    }
  })
}

export const useModifyAccountPerformanceStartDateMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ accountId, performanceStartDate }) => {
      const { data } = await postNamedCommand('accounts', 'updateAccountPerformanceStartDate', {
        accountId,
        performanceStartDate
      })

      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.searchAccounts], refetchType: 'all' }).catch(console.error)
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.searchAccountsMultiple],
        refetchType: 'all'
      }).catch(console.error)
    }
  })
}

export const useAssignAccountManagerMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ accountId, managerId }) => {
      const { data } = await postNamedCommand('accounts', 'assign-account-manager', {
        accountId,
        managerId
      })

      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.searchAccounts], refetchType: 'all' }).catch(console.error)
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.searchAccountsMultiple],
        refetchType: 'all'
      }).catch(console.error)
    }
  })
}

export const useRemoveAccountManagerMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ accountId }) => {
      const { data } = await postNamedCommand('accounts', 'remove-account-manager', {
        accountId
      })

      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.searchAccounts], refetchType: 'all' }).catch(console.error)
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.searchAccountsMultiple],
        refetchType: 'all'
      }).catch(console.error)
    }
  })
}

export const useManagerSearch = (query, options = {}) => {
  const { userId } = useAppContext()
  const { enabled = true, mapper } = options
  return useQuery({
    queryKey: [QUERY_KEYS.searchManagers, userId, query],
    queryFn: async () => {
      const { data } = await postNamedQuery('accounts', 'search-managers', query)

      return data
    },
    select: mapper,
    enabled
  })
}

export const useManagerById = (managerId, options = {}) => {
  const { userId } = useAppContext()
  const { enabled = true, mapper } = options
  return useQuery({
    queryKey: [QUERY_KEYS.getManagerById, userId, managerId],
    queryFn: async () => {
      const { data } = await postNamedQuery('accounts', 'get-manager-by-id', {
        managerId
      })

      return data
    },
    select: mapper,
    enabled
  })
}

export const useCreateManagerMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ manager }) => {
      const { data } = await postNamedCommand('accounts', 'create-manager', {
        manager
      })

      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.searchManagers], refetchType: 'all' }).catch(console.error)
    }
  })
}

export const useModifyManagerMutation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ manager }) => {
      const { data } = await postNamedCommand('accounts', 'modify-manager', {
        manager
      })

      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.searchManagers], refetchType: 'all' }).catch(console.error)
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.getManagerById], refetchType: 'all' }).catch(console.error)
    }
  })
}
